import { ElMessage } from 'element-plus'
import type { Router } from 'vue-router'
import { cancelAllRequests } from '@/api/request'
import { getToken } from '@/utils/auth'
import { useAppStore } from '@/stores/app'
import { useUserStore } from '@/stores/user'
import { translate } from '@/locales'
import { env } from '@/config/env'

/**
 * 路由守卫：登录校验 + 权限校验 + 页签/标题维护
 */
export function setupRouterGuard(router: Router): void {
  router.beforeEach(async (to) => {
    // 路由切换时取消未完成请求
    cancelAllRequests('route-change')

    const userStore = useUserStore()
    const appStore = useAppStore()
    const token = getToken()

    // 免登录页面
    if (to.meta.public) {
      if (to.path === '/login' && token) return { path: '/' }
      return true
    }

    if (!token || !userStore.isLogin) {
      return { path: '/login', query: to.fullPath !== '/' ? { redirect: to.fullPath } : undefined }
    }

    // 刷新后恢复用户信息
    if (!userStore.loaded) {
      try {
        await userStore.loadProfile()
      } catch {
        userStore.reset()
        return { path: '/login', query: { redirect: to.fullPath } }
      }
    }

    // 错误页不做权限校验
    if (to.path === '/403' || to.path === '/404') return true

    if (!userStore.hasPermission(to.meta.permission)) {
      ElMessage.warning(translate('error.forbiddenDesc'))
      return { path: '/403' }
    }

    if (!userStore.hasRole(to.meta.roles)) {
      return { path: '/403' }
    }

    // 小屏自动收起侧边栏
    if (appStore.device === 'mobile') appStore.sidebarCollapsed = true

    return true
  })

  router.afterEach((to) => {
    const appStore = useAppStore()
    appStore.addTag(to)
    const title = to.meta.titleKey ? translate(to.meta.titleKey) : ''
    document.title = title ? `${title} - ${env.appTitle}` : env.appTitle
  })

  router.onError((error) => {
    // 模块加载失败（发版后旧 chunk 失效）时提示刷新
    if (/Failed to fetch dynamically imported module|Importing a module script failed/i.test(error.message)) {
      ElMessage.error('页面资源已更新，请刷新页面')
    }
  })
}
